import * as moment from "moment";

import { API_URL } from "./config";
import { HttpException } from "./exceptions";
import { HTTPModule } from "./http";
import { Util } from "./util";

export class User extends HTTPModule {
  constructor() {
    super({
      baseURL: `${API_URL}/user`,
    });
  }

  async login(credentials) {
    const { data } = await this.post("/login", credentials);
    const { user, access_token } = data;
    user.token = access_token;

    return user;
  }

  async register(credentials) {
    const { message } = await this.post("/create", credentials);
    const { user, token } = message;
    user.token = token;

    return user;
  }

  async profile() {
    const { data: user } = await this.get("/profile");
    const token = localStorage.authToken;
    user.token = token;

    return user;
  }

  async resendOtp(payload) {
    await this.post("/email/otp/send", payload);
  }

  async verifyEmail(payload) {
    return this.post("/email/otp/verify", payload);
  }

  async verifyUserStatus() {
    const { message } = await this.get("/status");

    return message.includes("active");
  }

  async getUserStatus() {
    const {
      0: { data },
    } = await this.get("/status");

    return data;
  }

  async getUserPaymentOptions() {
    const {
      0: { data },
    } = await this.get("/status");

    return {
      card: +data.is_card === 1,
      crypto: +data.is_crypto === 1,
      dollar: +data.is_dollar === 1,
      transfer: +data.is_transfer === 1,
    };
  }

  async getCountries() {
    const { data } = await this.get("/countries");

    return data;
  }

  async getBanks() {
    const {
      data: [data],
    } = await this.get("/banks");

    return data;
  }

  async verifyAccountNumber(payload) {
    const [{ data }] = await this.post("/account/verify", payload);
    return data;
  }

  async getBalances() {
    const { data } = await this.get("/balances");

    return {
      ...data,
      ovest: Util.formatMoneyNumber(data.wallet_balance),
      savest: {
        total: Util.formatMoneyNumber(data.savest_total_balance),
        percentageChane: 0.3,
        totalReturns: Util.formatMoneyNumber(data.total_savest_return),
      },
      investment: {
        total: Util.formatMoneyNumber(data.total_amount_invested),
        percentageChane: 2,
        totalReturns: Util.formatMoneyNumber(data.total_investment_return),
      },
    };
  }

  async bankTransfer(payload) {
    const { status } = await this.post("/wallet/deposit/bank", payload);
    if (!status.includes("succes")) {
      throw new HttpException("bank transfer failed", 400);
    }
  }

  async initiateCard(payload) {
    const {
      data: { link },
    } = await this.post("/wallet/deposit/card", payload);
    console.log(payload);

    return link;
  }

  async getInvestmentStats() {
    const stats = await this.getBalances();

    return {
      totalAmountInvested: stats.total_amount_invested,
      totalInvestmentPackages: "0",
      totalReturns: stats.total_investment_return,
      portfolioChange: "0.00",
    };
  }

  async getUserInvestments() {
    const {
      data: { All_investments },
  } = await this.get("/investments");

    return All_investments;
  }

  async getUserMaturedInvestments() {
    const { data } = await this.get("/investments/matured");

    return data;
  }

  async activateCryptoAccount() {
    const { status } = await this.post("/cryptodeposit/activate");

    return status === "success";
  }

  async activateDollarAccount() {
    const { data } = await this.post("/dollarwallet/activate");

    return data;
  }

  async updateUserProfile(payload) {
    const {
      message: { data },
    } = await this.post("/profile/update", payload);

    return data;
  }

  async addBankAccount(payload) {
    const { data, status, message } = await this.post("/bank/add", payload);
    if (status !== "success") {
      throw new HttpException(message, 400);
    }

    return data;
  }

  async getSavedBanks() {
    const { data } = await this.get("/saved_banks");

    return data;
  }

  async deleteBankAccount(id) {
    await this.post(`/bank_account/delete/${id}`);
  }

  async getSavestTransactions() {
    const { data } = await this.get("/savest/transactions");

    return data;
  }

  async forgotPassword(email) {
    await this.post("/password/forgot", { email });
  }

  async resetPassword(payload) {
    await this.post("/password/reset", payload);
  }

  // async verifyCardTransaction(payload) {
  //   await this.get("/callback", payload);
  // }
  async verifyCardTransaction(transaction_id) {
    await this.get("/callback?transaction_id=" + transaction_id);
  }
  async getUserCards() {
    const { data } = await this.get("/cards");

    return data;
  }

  async withdrawToBank(payload) {
    await this.post("/withdraw_to_bank", payload);
  }

  async getUserRecentBanks() {
    const { data } = await this.get("/bank/recent");

    return Array.isArray(data) ? data : [data];
  }

  async withdrawToOvest(payload) {
    await this.post("/withdraw_to_ovestor", payload);
  }

  async getTransactions() {
    const {
      data: [data],
    } = await this.get("/transactions");

    return data.map((item) => ({
      ...item,
      dateResolved: moment(item.created_at),
      status: item.status || "Processed",
      amount: item.type === "Withdrawal" ? -1 * item.amount : item.amount,
    }));
  }

  async invest(payload) {
    await this.post("/invest", payload);
  }

  async changePassword(payload) {
    return this.post("/password/change", payload);
  }

  async getStates(countryId) {
    const { data } = await this.get(`/states/${countryId}`);

    return data;
  }

  async addNextOfKin(payload) {
    return this.post("/next_of_kin/add", payload);
  }

  async createSavestPlan(payload) {
    const { data } = await this.post("/savest_plan/create", payload);

    return data;
  }

  async getUserSavestPlans() {
    const { data } = await this.get("/savest/plans");

    return data;
  }

  async getUserSavestTransactions() {
    const { data } = await this.get("/savest/transactions");

    return data;
  }

  async withdrawSavest(savest_plan_id, amount) {
    return this.post("/savest/withdrawal", { amount, savest_plan_id });
  }

  async updateSavestPlanFromWallet(savest_plan_id, amount) {
    return this.post(`/savest/update/wallet/${savest_plan_id}`, { amount });
  }

  async topupSavest(payload) {
    return this.post("/savest/topup", payload);
  }

  async togglePaymentOptions(payload) {
    return this.post("/payment_option/enable", payload);
  }

  async getReferralDashboardData() {
    const { data } = await this.get("/referral/dashboard");

    return data;
  }

  async withdrawReferralEarnings() {
    return this.post("/referral/withdraw");
  }

  async verifyKYCStatus() {
    return this.get("/kyc/verify/status");
  }

  async addCryptoWallet(payload) {
    return this.post("/crypto/wallet/add", payload);
  }

  async getSavedWallets() {
    const { data } = await this.get("/crypto/wallets");

    return data;
  }

  async deleteCryptoWallet(id) {
    return this.post(`/crypto/wallet/delete/${id}`);
  }

  async deleteCard(id) {
    return this.post(`/card/delete/${id}`);
  }

  async dollarDepositCard(amount) {
    const { data } = await this.post("/dollar/deposit/card", { amount });

    return data;
  }

  async getUnreadNotifications() {
    const { data } = await this.get("/notifications/unread");

    return data;
  }

  async chargeSavedCard(payload) {
    return this.post(`/card/charge`, payload);
  }

  async markAllNotificationsRead() {
    return this.get("/notifications/mark_as_read");
  }
}

export class Investment extends HTTPModule {
  constructor() {
    super({
      baseURL: `${API_URL}/investment`,
    });
  }

  async getPackages() {
    const { data } = await this.get("/packages");

    return data;
  }

  async getPackageDetail(id) {
    const { data } = await this.get(`/details/${id}`);

    return data;
  }

  async getSpecialPackages() {
    const { data } = await this.get("/packages/special");

    return data;
  }
}

export class Savest extends HTTPModule {
  constructor() {
    super({
      baseURL: `${API_URL}/savest`,
    });
  }

  async getSavestEarnings(id) {
    const { data } = await this.get(`/plan/earnings/${id}`);

    return data;
  }
}

export class $api {
  static updateRequestConfig(config) {
    console.log(config);
    $api.user.updateRequestConfig(config);
  }

  static user = new User();

  static investment = new Investment();

  static savest = new Savest();
}
